import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AiOutlineSearch, AiOutlineClose } from "react-icons/ai";
import { searchAction } from "../redux/actions/search";
import { getUserWishlist } from "../redux/actions/wishlist";
import { NavLink, useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";
import logo from "/assets/images/main_logo.svg";
import { checkIsAdmin } from "../redux/actions/user";
import debounce from "lodash.debounce";
import { allCategoryAction } from "../redux/actions/categories";
import helper from "../Custom/CustomHelpers";
import { decode } from "../config";
import Cart from "./Cart";
import { getFirstImage } from "../utils/cartHelper";

const WEBSITE_NAME = decode(window._env_?.WEBSITE_NAME);

// Small reusable badge — used by both wishlist and cart, desktop and mobile
const CountBadge = ({ count }) => {
  if (!count || count <= 0) return null;
  return (
    <span className="flex absolute -top-1 -right-1">
      <span className="animate-ping absolute inline-flex h-4 w-4 rounded-full bg-pink-400 opacity-75" />
      <span className="relative inline-flex rounded-full h-4 w-4 bg-pink-500 items-center justify-center">
        <span className="text-white text-[8px] leading-none">{count}</span>
      </span>
    </span>
  );
};

const iconButtonClasses =
  "relative flex items-center justify-center h-10 w-10 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer";

const navLinkClasses = ({ isActive }) =>
  `relative py-1 transition-colors hover:text-custom-dark-green dark:hover:text-custom-green ${
    isActive
      ? "text-custom-dark-green dark:text-custom-green font-medium"
      : "text-current"
  }`;

function Navbar() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { cartItems } = useSelector((state) => state.cart);
  const { isAdmin } = useSelector((state) => state.admin);
  const [search, setSearch] = useState("");
  const [user, setUser] = useState(null);
  const isCartOpen = useSelector((state) => state.drawer?.drawer);
  const [isMenuOpen, setIsMenuOpen] = useState(false); // burger menu
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isAnonMenuOpen, setIsAnonMenuOpen] = useState(false);
  const [isCategoryHover, setIsCategoryHover] = useState(false);
  const [catalogueOpen, setCatalogueOpen] = useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const wishlistItems = useSelector(
    (state) => state.wishlist.wishlistProducts || [],
  );
  const searchResults = useSelector((state) => state.search);

  const menuRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    dispatch(allCategoryAction());
  }, [dispatch]);

  useEffect(() => {
    if (user) {
      dispatch(getUserWishlist({ productId: 0, userUID: user.uid }));
    }
  }, [dispatch, user]);

  // Subtle shadow once the page scrolls, so the fixed navbar
  // separates from content instead of floating with no edge
  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const { category } = useSelector((state) => state.category);
  const navbarCategories =
    category?.filter((cat) => cat.showInNavbar === true) || [];

  const dropdownCategories =
    category?.filter((cat) => cat.showInNavbar === false) || [];

  const auth = getAuth();
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser && !firebaseUser.isAnonymous) {
        setUser(firebaseUser);
        dispatch(checkIsAdmin(firebaseUser.uid));
      } else {
        setUser(null);
      }
    });
    return () => unsubscribe();
  }, [dispatch]);

  const debouncedSearch = useRef(
    debounce((query) => {
      dispatch(searchAction(query));
    }, 300),
  ).current;

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);

    if (value.trim() === "") {
      dispatch({ type: "SEARCH", payload: [] });
    } else {
      debouncedSearch(value);
    }
  };

  useEffect(() => {
    function handleClickOutside(e) {
      // Don't let the desktop click-outside handler
      // interfere with the mobile search overlay.
      if (isMobileSearchOpen) {
        return;
      }

      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSearch("");
        dispatch({ type: "SEARCH", payload: [] });
      }
    }

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dispatch, isMobileSearchOpen]);

  const dispatchHandler = () => {
    dispatch({ type: "DRAWER", payload: !isCartOpen });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    signOut(auth)
      .then(() => {
        navigate("/", { replace: true });
        window.location.reload(true);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const getProductItemPrice = (product) => {
    const offer = product.offer || product.offers || product.discountOffer;

    if (!offer || !offer.isActive || offer.offerType === "Quantity") {
      let price = product.basePrice;
      if (product.priceType === 1 && product.prices?.length) {
        const mainPrice = product.prices.find((p) => p.sortOrder === 1);
        price = mainPrice ? mainPrice.price : product.basePrice;
      }
      return { hasDiscount: false, price };
    }

    const discountPercent = offer.offerValue;
    let originalPrice = product.basePrice;
    if (product.priceType === 1 && product.prices?.length) {
      const mainPrice = product.prices.find((p) => p.sortOrder === 1);
      originalPrice = mainPrice ? mainPrice.price : product.basePrice;
    }
    const discountedPrice =
      originalPrice - (originalPrice * discountPercent) / 100;
    return {
      hasDiscount: true,
      originalPrice,
      discountedPrice,
      discountPercent,
    };
  };

  return (
    <div>
      <section className="mx-auto">
        <nav
          className={`fixed left-0 right-0 z-50 bg-custom-white dark:bg-custom-dark transition-shadow duration-200 ${
            localStorage.getItem("promobanner") === "true" ? "top-12" : "top-0"
          } ${isScrolled ? "shadow-sm border-b border-gray-200/70 dark:border-gray-800" : "border-b border-transparent"}`}
        >
          <div className="px-4 sm:px-6 xl:px-12 py-3 flex w-full items-center gap-4">
            <NavLink className="font-ibmplex text-lg shrink-0" to="/">
              <img
                src={logo}
                alt={`${WEBSITE_NAME} Logo`}
                className="h-16 md:h-14 w-auto"
                width={512}
                height={128}
                fetchPriority="high"
                loading="eager"
                decoding="async"
              />
            </NavLink>

            <ul className="hidden md:flex items-center px-2 mr-auto ml-8 font-poppins gap-7 text-sm">
              <li>
                <NavLink to="/" className={navLinkClasses}>
                  Home
                </NavLink>
              </li>
              <li
                className="relative"
                onMouseEnter={() => setIsCategoryHover(true)}
                onMouseLeave={() => setIsCategoryHover(false)}
              >
                <span className="hover:text-gray-600 dark:hover:text-gray-600 cursor-pointer flex items-center gap-1 transition-colors">
                  Category
                  <svg
                    className={`w-4 h-4 transform transition-transform duration-200 ${
                      isCategoryHover ? "rotate-180" : ""
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </span>

                <div
                  className={`absolute left-0 top-full mt-2 bg-white dark:bg-custom-dark-second shadow-lg rounded-xl w-48 z-[2000] overflow-y-auto max-h-64 transition-all duration-200 origin-top ${
                    isCategoryHover
                      ? "opacity-100 scale-100 pointer-events-auto"
                      : "opacity-0 scale-95 pointer-events-none"
                  }`}
                >
                  {dropdownCategories && dropdownCategories.length > 0 ? (
                    <>
                      {dropdownCategories.map((cat) => (
                        <NavLink
                          key={cat.id}
                          to={`/category/${helper.generateSlug(cat)}`}
                          className="block px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        >
                          {cat.title}
                        </NavLink>
                      ))}
                      <NavLink
                        key="all-products"
                        to={`/category/${helper.generateSlug({
                          id: -1,
                          title: "All Products",
                        })}`}
                        className="block px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700 font-semibold text-custom-dark-green dark:text-custom-green"
                      >
                        All Products
                      </NavLink>
                    </>
                  ) : (
                    <p className="px-4 py-2.5 text-gray-500 text-sm">
                      No categories
                    </p>
                  )}
                </div>
              </li>

              {navbarCategories.map((cat) => (
                <li key={cat.id}>
                  <NavLink
                    to={`/category/${helper.generateSlug(cat)}`}
                    className={navLinkClasses}
                  >
                    {cat.title}
                  </NavLink>
                </li>
              ))}

              <li>
                <NavLink to="/contactus" className={navLinkClasses}>
                  Contact Us
                </NavLink>
              </li>
              <li>
                <NavLink to="/aboutus" className={navLinkClasses}>
                  About Us
                </NavLink>
              </li>
            </ul>

            {/* Search — desktop */}
            <div
              ref={searchRef}
              className="hidden md:flex md:flex-1 relative md:ml-4 md:w-[360px] max-w-[360px]"
            >
              <AiOutlineSearch
                className="absolute top-1/2 -translate-y-1/2 left-3 text-gray-400"
                size={16}
              />
              <input
                value={search}
                onChange={handleSearchChange}
                className="w-full rounded-full border border-gray-300 dark:border-gray-600 bg-custom-white dark:bg-custom-dark py-2 pl-9 pr-4 text-base focus:outline-none focus:ring-2 focus:ring-custom-green/40 focus:border-custom-green transition-shadow"
                type="text"
                placeholder="Search"
              />

              {search && searchResults?.length > 0 && (
                <div className="absolute top-full left-0 right-0 bg-white dark:bg-custom-dark-second shadow-lg rounded-xl mt-2 z-50 max-h-80 overflow-y-auto">
                  {searchResults.map((product) => {
                    const price = getProductItemPrice(product);
                    const offer =
                      product.offer || product.offers || product.discountOffer;
                    const isQuantityOffer = offer?.offerType === "Quantity";
                    const isPercentageOffer = offer?.offerType === "Percentage";

                    return (
                      <div
                        key={product.id}
                        className="relative flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                        onClick={() =>
                          navigate(`/product/${helper.generateSlug(product)}`)
                        }
                      >
                        {(isQuantityOffer || isPercentageOffer) && (
                          <span className="absolute top-2 right-2 bg-custom-green text-white text-[10px] font-semibold px-2 py-[2px] rounded shadow">
                            {isQuantityOffer
                              ? "Bulk Offer"
                              : `Save ${offer.offerValue}%`}
                          </span>
                        )}

                        <img
                          src={getFirstImage(product.image)}
                          alt={product.title}
                          className="w-10 h-14 object-cover rounded-md shrink-0"
                        />

                        <div className="flex flex-col min-w-0">
                          <span className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                            {product.title}
                          </span>

                          <span className="text-xs flex items-center gap-2">
                            {price.hasDiscount ? (
                              <>
                                <span className="line-through text-gray-400 dark:text-gray-500">
                                  Rs. {price.originalPrice}
                                </span>
                                <span className="text-custom-dark-green dark:text-custom-green font-semibold">
                                  Rs. {price.discountedPrice}
                                </span>
                              </>
                            ) : (
                              <span className="text-gray-900 dark:text-white font-semibold">
                                Rs. {price.price}
                              </span>
                            )}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Icons — desktop */}
            <div className="hidden xl:flex items-center gap-1">
              <button
                aria-label="Wishlist"
                onClick={() =>
                  dispatch({ type: "WISHLIST_DRAWER", payload: true })
                }
                className={iconButtonClasses}
              >
                <svg
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1.5"
                    d="M4.318 6.318a4.5 4.5 0 016.364 0L12 7.636l1.318-1.318a4.5 4.5 0 116.364 6.364L12 21.364l-7.682-7.682a4.5 4.5 0 010-6.364z"
                  />
                </svg>
                <CountBadge count={wishlistItems?.length} />
              </button>

              <button
                aria-label="Cart"
                onClick={dispatchHandler}
                className={iconButtonClasses}
              >
                <svg
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1.5"
                    d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
                <CountBadge count={cartItems?.length} />
              </button>

              {user ? (
                <div
                  className="relative"
                  onMouseEnter={() => setIsUserMenuOpen(true)}
                  onMouseLeave={() => setIsUserMenuOpen(false)}
                >
                  <button className={iconButtonClasses} aria-label="User menu">
                    {user?.photoURL ? (
                      <img
                        src={user.photoURL}
                        alt="User Profile"
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="1.5"
                          d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    )}
                  </button>

                  <div
                    className={`absolute right-0 top-full mt-2 w-56 rounded-2xl bg-white dark:bg-custom-dark-second shadow-xl ring-1 ring-black/10 dark:ring-white/10 z-[1000] transition-all duration-200 origin-top-right ${
                      isUserMenuOpen
                        ? "opacity-100 scale-100 pointer-events-auto"
                        : "opacity-0 scale-95 pointer-events-none"
                    }`}
                  >
                    <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-600">
                      <p className="font-semibold text-sm text-gray-800 dark:text-gray-200">
                        Hi, {user?.displayName || "Guest"}
                      </p>
                    </div>
                    <ul className="flex flex-col py-1">
                      <NavLink
                        to="/my-orders"
                        className="px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        My Orders
                      </NavLink>
                      {isAdmin && (
                        <>
                          <NavLink
                            to="/category"
                            className="px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                          >
                            Category
                          </NavLink>
                          <NavLink
                            to="/all-products"
                            className="px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                          >
                            All Products
                          </NavLink>
                          <NavLink
                            to="/promos"
                            className="px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                          >
                            Promocodes
                          </NavLink>
                          <NavLink
                            to="/all-orders"
                            className="px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                          >
                            All Orders
                          </NavLink>
                        </>
                      )}
                      <button
                        onClick={handleLogout}
                        aria-label="Sign Out"
                        className="w-full text-left px-4 py-2.5 text-red-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        Sign Out
                      </button>
                    </ul>
                  </div>
                </div>
              ) : (
                <div
                  className="relative"
                  onMouseEnter={() => setIsAnonMenuOpen(true)}
                  onMouseLeave={() => setIsAnonMenuOpen(false)}
                >
                  <button className={iconButtonClasses} aria-label="User menu">
                    <svg
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1.5"
                        d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </button>

                  <div
                    className={`absolute right-0 top-full mt-2 w-56 rounded-2xl bg-white dark:bg-custom-dark-second shadow-xl ring-1 ring-black/10 dark:ring-white/10 z-[1000] transition-all duration-200 origin-top-right ${
                      isAnonMenuOpen
                        ? "opacity-100 scale-100 pointer-events-auto"
                        : "opacity-0 scale-95 pointer-events-none"
                    }`}
                  >
                    <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-600">
                      <p className="font-semibold text-sm text-gray-800 dark:text-gray-200">
                        Hi, Guest
                      </p>
                    </div>
                    <ul className="flex flex-col py-1">
                      <NavLink
                        to="/my-orders"
                        className="px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        My Orders
                      </NavLink>
                      <NavLink
                        to="/login"
                        className="px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        Log In
                      </NavLink>
                      <NavLink
                        to="/signup"
                        className="px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        Sign Up
                      </NavLink>
                    </ul>
                  </div>
                </div>
              )}
            </div>

            {/* Icons — mobile/tablet, grouped so spacing stays consistent */}
            <div className="flex items-center gap-0.5 xl:hidden ml-auto">
              <button
                aria-label="Search"
                onClick={() => setIsMobileSearchOpen(true)}
                className={iconButtonClasses}
              >
                <AiOutlineSearch size={18} />
              </button>

              <button
                aria-label="Wishlist"
                onClick={() =>
                  dispatch({ type: "WISHLIST_DRAWER", payload: true })
                }
                className={iconButtonClasses}
              >
                <svg
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1.5"
                    d="M4.318 6.318a4.5 4.5 0 016.364 0L12 7.636l1.318-1.318a4.5 4.5 0 116.364 6.364L12 21.364l-7.682-7.682a4.5 4.5 0 010-6.364z"
                  />
                </svg>
                <CountBadge count={wishlistItems?.length} />
              </button>

              <button
                aria-label="Cart"
                onClick={dispatchHandler}
                className={iconButtonClasses}
              >
                <svg
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1.5"
                    d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
                <CountBadge count={cartItems?.length} />
              </button>

              <button
                className={`navbar-burger ${iconButtonClasses}`}
                aria-label="Menu"
                aria-expanded={isMenuOpen}
                onClick={() => setIsMenuOpen(!isMenuOpen)}
              >
                {isMenuOpen ? (
                  <AiOutlineClose size={18} />
                ) : (
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.5"
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </nav>

        {/* Spacer so page content doesn't sit under the fixed nav */}
        <div className="h-[68px] md:h-[76px]" />

        {isMenuOpen && (
          <div
            ref={menuRef}
            className="fixed right-3 top-[68px] md:top-[76px] w-64 max-h-[75vh] overflow-y-auto rounded-2xl bg-white dark:bg-custom-dark-second shadow-xl ring-1 ring-black/10 dark:ring-white/10 z-[1000] animate-fadeIn"
          >
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-600">
              <p className="font-semibold text-sm text-gray-800 dark:text-gray-200">
                Hi, {user?.displayName || "Guest"}
              </p>
            </div>
            <ul className="flex flex-col py-1">
              <NavLink
                to="/"
                className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Home
              </NavLink>

              <li className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors">
                <div
                  className="flex justify-between items-center"
                  onClick={() => setCatalogueOpen(!catalogueOpen)}
                >
                  <span>Category</span>
                  <svg
                    className={`w-4 h-4 transition-transform duration-200 ${
                      catalogueOpen ? "rotate-180" : ""
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
                {catalogueOpen && (
                  <ul className="pl-3 mt-2 flex flex-col space-y-1 border-l border-gray-200 dark:border-gray-700">
                    {dropdownCategories && dropdownCategories.length > 0 ? (
                      <>
                        {dropdownCategories.map((cat) => (
                          <NavLink
                            key={cat.id}
                            to={`/category/${helper.generateSlug(cat)}`}
                            className="block px-3 py-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-sm"
                          >
                            {cat.title}
                          </NavLink>
                        ))}
                        <NavLink
                          key="all-products"
                          to={`/category/${helper.generateSlug({ id: -1, title: "All Products" })}`}
                          className="block px-3 py-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 font-semibold text-custom-dark-green dark:text-custom-green text-sm"
                        >
                          All Products
                        </NavLink>
                      </>
                    ) : (
                      <p className="px-3 py-2 text-gray-500 text-sm">
                        No categories
                      </p>
                    )}
                  </ul>
                )}
              </li>

              {navbarCategories.map((cat) => (
                <NavLink
                  key={cat.id}
                  to={`/category/${helper.generateSlug(cat)}`}
                  className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  {cat.title}
                </NavLink>
              ))}

              <NavLink
                to="/my-orders"
                className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                My Orders
              </NavLink>
              <NavLink
                to="/contactus"
                className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Contact Us
              </NavLink>
              <NavLink
                to="/aboutus"
                className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                About Us
              </NavLink>

              {isAdmin && user && (
                <>
                  <li className="px-4 pt-2 pb-1 text-xs uppercase tracking-wide text-gray-400">
                    Admin
                  </li>
                  <NavLink
                    to="/category"
                    className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    Category
                  </NavLink>
                  <NavLink
                    to="/all-products"
                    className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    All Products
                  </NavLink>
                  <NavLink
                    to="/promos"
                    className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    Promocodes
                  </NavLink>
                  <NavLink
                    to="/all-orders"
                    className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    All Orders
                  </NavLink>
                </>
              )}

              <li className="border-t border-gray-200 dark:border-gray-700 mt-1 pt-1">
                {user ? (
                  <button
                    onClick={handleLogout}
                    aria-label="Sign Out"
                    className="w-full text-left px-4 py-3 text-red-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    Sign Out
                  </button>
                ) : (
                  <>
                    <NavLink
                      to="/login"
                      className="block px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      Log In
                    </NavLink>
                    <NavLink
                      to="/signup"
                      className="block px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      Sign Up
                    </NavLink>
                  </>
                )}
              </li>
            </ul>
          </div>
        )}

        {isMobileSearchOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-[9999] flex items-start justify-center p-4 pt-20"
            onClick={() => setIsMobileSearchOpen(false)}
          >
            <div
              className="bg-white dark:bg-custom-dark rounded-2xl w-full max-w-md p-4 shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-2 mb-3">
                <div className="relative flex-1">
                  <AiOutlineSearch
                    className="absolute top-1/2 -translate-y-1/2 left-3 text-gray-400"
                    size={16}
                  />
                  <input
                    type="text"
                    value={search}
                    onChange={handleSearchChange}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-full py-2 pl-9 pr-3 text-base focus:outline-none focus:ring-2 focus:ring-custom-green/40 focus:border-custom-green transition-shadow"
                    placeholder="Search products..."
                    autoFocus
                  />
                </div>
                <button
                  aria-label="Close search"
                  onClick={() => setIsMobileSearchOpen(false)}
                  className={iconButtonClasses}
                >
                  <AiOutlineClose size={16} />
                </button>
              </div>

              {search && searchResults?.length > 0 && (
                <div className="max-h-80 overflow-y-auto -mx-1">
                  {searchResults.map((product) => {
                    const price = getProductItemPrice(product); // ← add this
                    return (
                      <div
                        key={product.id}
                        className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                        onClick={() => {
                          const slug = helper.generateSlug(product);

                          setIsMobileSearchOpen(false);
                          setSearch("");
                          dispatch({ type: "SEARCH", payload: [] });

                          navigate(`/product/${slug}`);
                        }}
                      >
                        <img
                          src={getFirstImage(product.image)}
                          alt={product.title}
                          className="w-12 h-12 object-cover rounded-lg"
                        />
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">
                            {product.title}
                          </p>
                          <p className="text-xs flex items-center gap-2">
                            {price.hasDiscount ? (
                              <>
                                <span className="line-through text-gray-400 dark:text-gray-500">
                                  Rs. {price.originalPrice}
                                </span>
                                <span className="text-custom-dark-green dark:text-custom-green font-semibold">
                                  Rs. {price.discountedPrice}
                                </span>
                              </>
                            ) : (
                              <span className="text-gray-900 dark:text-white font-semibold">
                                Rs. {price.price}
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {isCartOpen && (
          <>
            <div
              className="fixed inset-0 bg-black/40 z-[999]"
              onClick={() => dispatch({ type: "DRAWER", payload: false })}
              aria-hidden="true"
            />
            <Cart
              closeCart={() => dispatch({ type: "DRAWER", payload: false })}
            />
          </>
        )}
      </section>
    </div>
  );
}

export default Navbar;
